import Analytics from '@/utils/analytics';
import PauseIcon from '@assets/icons/pause.svg';
import PlayIcon from '@assets/icons/play.svg';
import React from 'react';
import { AudioPlayerState } from '@/hooks/use-audio-player';
import { createStyles } from '@/contexts/theme-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { PEPPER_GREEN, TYPOGRAPHY } from '@/styles';
import { Slider } from 'react-native-awesome-slider';
import { Text, TouchableOpacity, View } from 'react-native';

interface Props {
  audioPlayerState: AudioPlayerState;
}

const formatMSAsTime = (ms: number | null) => {
  if (typeof ms !== 'number' || isNaN(ms)) {
    return '--:--';
  }
  const minutes = Math.floor(ms / 60000);
  const seconds = ((ms % 60000) / 1000).toFixed(0);
  return `${minutes}:${seconds.padStart(2, '0')}`;
};

const SLIDER_HEIGHT = 5;

export const AudioPlayer: React.FC<Props> = ({ audioPlayerState }) => {
  const {
    max,
    min,
    onPlayPausePress,
    onSlidingComplete,
    onSlidingStart,
    progress,
    status,
  } = audioPlayerState;
  const [styles, theme] = useStyles();

  const progressText = formatMSAsTime(status?.positionMillis ?? 0);
  const durationText = formatMSAsTime(status?.durationMillis ?? null);
  const timeText = `${progressText} / ${durationText}`;

  const IconComponent = status?.isPlaying ? PauseIcon : PlayIcon;

  return (
    <View style={styles.base}>
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={() => {
          Analytics.track('audio_player.play_pause_button.press', {
            action: status?.isPlaying ? 'pause' : 'play',
            position: status?.positionMillis,
          });
          onPlayPausePress();
        }}
        style={styles.playButton}
        testID={status?.isPlaying ? 'pause-icon' : 'play-icon'}
      >
        <IconComponent height={12} stroke="white" width={12} />
      </TouchableOpacity>
      <View style={styles.sliderRoot}>
        <GestureHandlerRootView>
          <Slider
            bubble={formatMSAsTime}
            containerStyle={styles.sliderContainer}
            // trigger rerender when duration is set to fix bug on web
            key={status?.durationMillis ? 'unset-duration' : 'set-duration'}
            maximumValue={max}
            minimumValue={min}
            onSlidingComplete={onSlidingComplete}
            onSlidingStart={onSlidingStart}
            progress={progress}
            sliderHeight={SLIDER_HEIGHT}
            testID="audio-player-slider"
            theme={{
              bubbleBackgroundColor: theme.colors.primary,
              cacheTrackTintColor: theme.sk.color.border.primary,
              disableMinTrackTintColor: theme.sk.color.border.primary,
              maximumTrackTintColor: theme.sk.color.border.primary,
              minimumTrackTintColor: PEPPER_GREEN,
            }}
            thumbWidth={SLIDER_HEIGHT}
          />
        </GestureHandlerRootView>
      </View>
      <Text style={[TYPOGRAPHY.xsmall, styles.timeText]}>{timeText}</Text>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  playButton: {
    alignItems: 'center',
    backgroundColor: theme.colors.primary,
    borderRadius: 24 / 2,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  sliderContainer: {
    borderRadius: SLIDER_HEIGHT / 2,
    height: SLIDER_HEIGHT,
  },
  sliderRoot: {
    flex: 1,
    marginHorizontal: 16,
  },
  timeText: {
    lineHeight: 14,
  },
}));
